import React, { useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, Text, View, TouchableOpacity } from 'react-native';
import { useUnistyles } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import DatePickerInput from '@/components/General/inputs/DatePickerInput'
import SheetSelector from '@/components/General/inputs/SheetSelector';
import InputSection from '@/components/General/inputs/InputSection';
import { saveEventInstance, checkLocationConflicts, buildLocationCheckRanges, describeLocationConflicts } from '@/data/events';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import CustomField from '@/components/CustomFields/CustomField';
import Callout from '@/components/General/Callout';
import SolidButton from '@/components/General/SolidButton';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import ToggleInput from '@/components/General/inputs/ToggleInput';
import Toast from 'react-native-toast-message';
import { useEditScreenStore } from '@/data/store/useEditScreenStore';
import { redirectToDefault } from '@/utils/constants/linking';
import { useLocalSearchParams, usePathname, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import LocationScheduleModal from '@/components/Events/LocationScheduleModal';
import Unicon from '@/components/General/Unicon';
import TooltipContainer from '@/components/General/Tooltip';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import Toggle from '@/components/General/inputs/Toggle';
import SelectInput from '@/components/General/inputs/SelectInput';

const EMPTY_DRAFT = {};

export default function EditEventInstanceModal() {
    const { id } = useLocalSearchParams();
    const storeKey = id || "new"

    const [ scheduleModalOpen, setScheduleModalOpen ] = useState(false);
    const [ locationConflicts, setLocationConflicts ] = useState([]);

    const setEditScreenData = useEditScreenStore(s => s.setEditScreenData)
    const removeEditScreenData = useEditScreenStore(s => s.removeEditScreenData)
    const eventDetails = useEditScreenStore(s => s.EditEventInstanceModal?.[storeKey]) || EMPTY_DRAFT

    const {theme: {colors, fonts}} = useUnistyles();
    const queryClient = useQueryClient()
    const router = useRouter()
    const pathname = usePathname()

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: eventCustomFields } = Query.eventCustomFields.all();
    const { data: eventToEdit, isLoading } = Query.eventInstances.byId({id: id, options: { enabled: !!id }});
    const { data: extensionSource } = Query.eventInstances.registrationExtensionSource({ eventInstanceID: id, options: { enabled: !!id } });
    const { data: printLabels } = Query.printLabels.all();
    // Fetch paths with EVENT_CHECK_IN trigger for the Check In Path picker
    const eventCheckInPathsQuery = Query.paths.forEventCheckInTrigger();
    const eventCheckInPaths = eventCheckInPathsQuery.data;

    // Resolve the currently selected check-in path object (for SheetSelector display)
    const selectedCheckInPath = useMemo(() => {
        const pathId = eventDetails.checkInPathID;
        if (!pathId || !eventCheckInPaths) return null;
        const found = eventCheckInPaths.find((p) => p.id === pathId);
        return found || { id: pathId, name: 'Selected Automation' };
    }, [eventDetails.checkInPathID, eventCheckInPaths]);

    const saveEventInstanceMutation = useMutation({
        mutationFn: saveEventInstance,
        onSuccess: (data) => {
            Toast.show({text2: "Event successfully updated!",type: "success"});
            queryClient.invalidateQueries({queryKey: QueryKeys.events.all()});
            queryClient.invalidateQueries({queryKey: QueryKeys.eventInstances.byId(data.id)});
            queryClient.invalidateQueries({queryKey: QueryKeys.serviceFlows.all()});
            removeEditScreenData("EditEventInstanceModal", storeKey)
            redirectToDefault(router, "EditEventInstanceModal")
        },
        onError: (error) => {
            if (error?.eventInstanceID) {
                setEventDetails({ id: error.eventInstanceID });
                Toast.show({ type: 'error', text2: 'This occurrence exists, but finishing setup failed. Press Save to retry.' });
            }
        },
    })

    // Snapshot of the loaded draft — powers the "save/discard before opening
    // the Service Flow tab" hint (spec §3.5: date editing lives there).
    const initialDetailsRef = useRef(null);
    const hasUnsavedChanges = useMemo(() => {
        if (!initialDetailsRef.current) return false;
        return JSON.stringify(eventDetails) !== initialDetailsRef.current;
    }, [eventDetails]);

    useEffect(() => {
        if(!isLoading) {
            if(eventToEdit){
                const customFields = eventToEdit.customFieldValues ? eventToEdit.customFieldValues : eventToEdit.Event?.defaultCustomFieldValues ? eventToEdit.Event?.defaultCustomFieldValues : "{}"
                const existingSpeakers = eventToEdit.speakers && eventToEdit.speakers?.length > 0
                    ? eventToEdit.speakers
                    : eventToEdit.Event?.defaultSpeaker?.id
                        ? [eventToEdit.Event?.defaultSpeaker]
                        : []

                const loaded = {
                    ...eventToEdit,
                    speakers: existingSpeakers,
                    startTime: eventToEdit.startTime ? new Date(eventToEdit.startTime) : null,
                    endTime: eventToEdit.endTime ? new Date(eventToEdit.endTime) : null,
                    customFieldValues: customFields,
                    songs: eventToEdit.Songs,
                    links: eventToEdit.Links,
                };
                setEventDetails(loaded);
                initialDetailsRef.current = JSON.stringify(loaded);
            }
        }
    }, [isLoading]);

    const setEventDetails = (new_details) => {
        setEditScreenData("EditEventInstanceModal", storeKey, new_details)
    }

    // Shared by the SheetSelector and the room-schedule modal so existing
    // per-location config (print labels, check-in filters) is preserved.
    const applyLocationOverrides = (selected_items) => {
        const existingById = {};
        (eventDetails.LocationOverrides || []).forEach(l => { existingById[l.id] = l; });
        const merged = (selected_items || []).map(l => ({
            ...l,
            printLabelID: existingById[l.id]?.printLabelID || null,
            printFamilyLabelID: existingById[l.id]?.printFamilyLabelID || null,
            checkInFilterID: existingById[l.id]?.checkInFilterID || null,
            CheckInFilter: existingById[l.id]?.CheckInFilter || null
        }));
        setEventDetails({
            LocationOverrides: merged
        });
    };

    const toggleLocationFromSchedule = (loc) => {
        const current = eventDetails.LocationOverrides || [];
        const exists = current.find(l => l.id === loc.id);
        applyLocationOverrides(exists ? current.filter(l => l.id !== loc.id) : [...current, loc]);
    };

    // Debounced heads-up: warn (don't block) when the effective locations are
    // already booked at this occurrence's times. Mirrors the checks in
    // saveEventInstance — overrides win, else the series' locations.
    const toISOorNull = (v) => (v && !isNaN(new Date(v)) ? new Date(v).toISOString() : null);
    const conflictCheckKey = JSON.stringify({
        o: (eventDetails.LocationOverrides || []).map(l => l?.id),
        sl: (eventDetails.Event?.Locations || []).map(l => l?.locationID || l?.Location?.id || l?.id),
        sd: eventDetails.Event?.defaultLocationID || null,
        se: eventDetails.Event?.locationID || null,
        li: eventDetails.locationID || null,
        s: toISOorNull(eventDetails.startTime),
        e: toISOorNull(eventDetails.endTime),
        a: !!eventDetails.allDay,
    });

    useEffect(() => {
        const overrideIDs = (eventDetails.LocationOverrides || []).map(l => l?.id).filter(Boolean);
        const seriesIDs = [
            ...(eventDetails.Event?.Locations || []).map(l => l?.locationID || l?.Location?.id || l?.id),
            eventDetails.Event?.defaultLocationID,
            eventDetails.Event?.locationID,
            eventDetails.locationID,
        ].filter(Boolean);
        const locationIDs = overrideIDs.length > 0 ? overrideIDs : seriesIDs;
        const ranges = buildLocationCheckRanges({
            startTime: eventDetails.startTime,
            endTime: eventDetails.endTime,
            allDay: eventDetails.allDay,
        });
        if (!locationIDs.length || !ranges.length) {
            setLocationConflicts([]);
            return;
        }
        let cancelled = false;
        const t = setTimeout(() => {
            checkLocationConflicts({ locationIDs, ranges, excludeInstanceIDs: eventDetails.id ? [eventDetails.id] : id ? [id] : null })
                .then((conflicts) => { if (!cancelled) setLocationConflicts(conflicts); });
        }, 400);
        return () => { cancelled = true; clearTimeout(t); };
    }, [conflictCheckKey, id, eventDetails.id]);

    const styles = StyleSheet.create({
        addTimeButton: {
            borderRadius:8, 
            paddingVertical:5, 
            paddingHorizontal:8,
            minWidth:70,
            width:"15%",
            margin:3,
            alignItems:"center",
            backgroundColor:colors.surfaceVariant
        },
        scheduleLink: {
            flexDirection: 'row',
            alignItems: 'center',
            alignSelf: 'flex-start',
            gap: 6,
            paddingVertical: 6,
            paddingHorizontal: 4,
        },
        scheduleLinkText: {
            color: colors.primary,
            fontFamily: fonts.heading,
            fontSize: 13,
        }
    })

    const calcEndDate = (startDate, add_hours, add_minutes = 0, returnValue = false) => {
        if(!startDate){
            return false;
        }

        let endDate = new Date(startDate);
        endDate.setHours(endDate.getHours() + parseInt(add_hours));
        endDate.setMinutes(endDate.getMinutes() + parseInt(add_minutes));

        if(returnValue){
            return endDate;
        }
        else{
            setEventDetails({
                endTime: endDate
            })
        }
    }

    const customFields = eventCustomFields?.map((field) => {
        return <CustomField 
            key={field.name}
            fieldType={field.type} 
            onUpdate={(new_value) => {
                let old_custom_values = eventDetails.customFieldValues ? eventDetails.customFieldValues : {}

                setEventDetails({
                    customFieldValues: {
                        ...old_custom_values,
                        [field.name]: new_value
                    }
                })
            }} 
            options={{
                title: field.name,
                value: eventDetails.customFieldValues?.[field.name]
            }} 
        /> 
    });


    const newKeyInputs = eventDetails.songs?.map(s => {
        return (
            <Input 
                key={s.id}
                title={s.name + ' Key'}
                value={s.key == undefined ? s.defaultKey : s.key}
                onUpdate={(new_value) => {
                    setEventDetails(old => {
                        const newSongs = [...old.songs];
                        const currentSongIndex = newSongs.findIndex(n => n.id == s.id);

                        const validated = new_value.match(/[A-G](b|#)?/g)
                        new_value = validated ? validated[0] : "";

                        newSongs[currentSongIndex].key = new_value;

                        return {
                            songs: newSongs
                        }
                    })
                }} 
            />
        );
    })


    return (
        <ModalWrapper
            loading={saveEventInstanceMutation.isPending} 
            onConfirm={() => saveEventInstanceMutation.mutate(eventDetails)} 
            title={eventDetails?.Event?.name}
        >
            <LocationScheduleModal
                visible={scheduleModalOpen}
                onClose={() => setScheduleModalOpen(false)}
                date={eventDetails.startTime || eventDetails.Event?.defaultStartTime || new Date()}
                selectedLocationIDs={(eventDetails.LocationOverrides || []).map(l => l.id)}
                onToggleLocation={toggleLocationFromSchedule}
                selectable
                highlightStart={eventDetails.startTime}
                highlightEnd={eventDetails.endTime}
                highlightAllDay={!!eventDetails.allDay}
            />
            <KeyboardAwareScrollView bottomOffset={100} keyboardShouldPersistTaps="handled" contentContainerStyle={{alignItems:"center"}}>
                <View style={{paddingBottom:20, width: "100%"}}>

                    <View style={{paddingBottom:20}}>
                        <Text style={{color:colors.text, fontFamily:fonts.heading, fontSize:18, textAlign:"center"}}>{new Date(eventDetails.startTime || eventDetails.Event?.defaultStartTime).toLocaleString('default', {weekday:"long", month:"long", day: "2-digit", year:"numeric"})}</Text>
                        <Text style={{color:colors.textSecondary, fontFamily:fonts.heading, fontSize:14, textAlign:"center", paddingTop:5}}>{new Date(eventDetails.startTime || eventDetails.Event?.defaultStartTime).toLocaleString('default', {hour:"numeric", minute:"2-digit", hour12:true})}</Text>
                    </View>

                    <Callout type="info" message={`You are editing a single occurence of the recurring event "${eventDetails?.Event?.name}".`}/>   

                    <InputSection title="Basic Info" description="Enter the general information for your event.">
                        <Input
                            key="name"
                            title="Event Name"
                            maxLength={120}
                            value={eventDetails.nameOverride == null || eventDetails.nameOverride == undefined ? eventDetails.Event?.name : eventDetails.nameOverride}
                            onUpdate={(new_value) => {
                                setEventDetails({
                                    nameOverride: new_value
                                })
                            }}
                        />

                        <SheetSelector helpText="If you want different locations specifically for this event, you can add them here. They will overwrite the default locations." multiple type="locations" title="Location Overrides" addScreenName="events/locations/create" fromScreen={pathname} fromStoreKey={storeKey} value={eventDetails.LocationOverrides ?? []} onChange={(selected_item) => {
                            applyLocationOverrides(selected_item);
                        }} />

                        <TouchableOpacity testID="view-room-schedule" style={styles.scheduleLink} onPress={() => setScheduleModalOpen(true)}>
                            <Unicon name="calendar-outline" size={15} colorVariant="primary" />
                            <Text style={styles.scheduleLinkText}>View room schedule</Text>
                        </TouchableOpacity>

                        {locationConflicts.length > 0 && (
                            <View testID="location-conflict-warning" style={{ marginTop: 8 }}>
                                <Callout
                                    type="warning"
                                    title="Location conflict"
                                    message={`Another event is already booked in this location:\n${describeLocationConflicts(locationConflicts)}`}
                                />
                            </View>
                        )}

                        {eventDetails.LocationOverrides?.map((location) => (
                            <View key={location.id} style={{paddingTop:10, paddingBottom:10}}>
                                <Text style={{fontFamily:fonts.heading, color:colors.text, fontSize:14}}>{location.name}</Text>
                                <View style={{flexDirection:"row", alignItems:"center", justifyContent:"center", gap:10}}>
                                    <View style={{width:"50%"}}>
                                        <SelectInput
                                            title="Individual Label"
                                            value={printLabels?.find(l => l.id == location.printLabelID)?.name}
                                            options={printLabels?.map(label => label.name)}
                                            onUpdate={(new_value) => {
                                                const labelID = printLabels?.find(l => l.name == new_value)?.id || null;
                                                setEventDetails({
                                                    LocationOverrides: eventDetails.LocationOverrides.map(l =>
                                                        l.id === location.id ? {...l, printLabelID: labelID} : l
                                                    )
                                                });
                                            }}
                                        />
                                    </View>
                                    <View style={{width:"50%"}}>
                                        <SelectInput
                                            title="Family Label"
                                            value={printLabels?.find(l => l.id == location.printFamilyLabelID)?.name}
                                            options={printLabels?.map(label => label.name)}
                                            onUpdate={(new_value) => {
                                                const labelID = printLabels?.find(l => l.name == new_value)?.id || null;
                                                setEventDetails({
                                                    LocationOverrides: eventDetails.LocationOverrides.map(l =>
                                                        l.id === location.id ? {...l, printFamilyLabelID: labelID} : l
                                                    )
                                                });
                                            }}
                                        />
                                    </View>
                                </View>
                                <SheetSelector
                                    type="quickFilters"
                                    title={`${location.name} Filter`}
                                    fromScreen={pathname}
                                    fromStoreKey={storeKey}
                                    value={location.CheckInFilter}
                                    onChange={(selected_item) => {
                                        setEventDetails({
                                            LocationOverrides: eventDetails.LocationOverrides.map(l =>
                                                l.id === location.id
                                                    ? {...l, checkInFilterID: selected_item?.id || null, CheckInFilter: selected_item || null}
                                                    : l
                                            )
                                        });
                                    }}
                                />
                            </View>
                        ))}

                        <SheetSelector type="people" multiple={true} title="Speakers" addScreenName={loggedInUser?.can?.("Create People") ? "people/create" : null} fromScreen={pathname} fromStoreKey={storeKey} value={eventDetails.speakers} onChange={(selected_item) => {
                            setEventDetails({
                                speakers: selected_item
                            });
                        }} />


                        <View>
                            <Input
                                key="notes"
                                title="Notes"
                                value={eventDetails.notes || eventDetails.Event?.defaultNotes}
                                multiline
                                onUpdate={(new_value) => {
                                    setEventDetails({
                                        notes: new_value
                                    })
                                }}
                            />
                        </View>

                        <SheetSelector
                            type="paths"
                            title="Check-in Automation"
                            placeholder="Select an Automation"
                            helpText="Enroll attendees into this path when they check in to this event instance."
                            queryResult={eventCheckInPathsQuery}
                            value={selectedCheckInPath}
                            onChange={(selected_item) => {
                                setEventDetails({
                                    checkInPathID: selected_item?.id || null
                                });
                            }}
                            testID="event-checkin-path"
                        />
                    </InputSection>


                    <InputSection title="Links" description="Add relevant links to this event from your organiations link directory.">
                        <SheetSelector type="links" multiple={true} title="Links" addScreenName={loggedInUser?.can?.("Create Links") ? "library/links/create" : null} fromScreen={pathname} fromStoreKey={storeKey} value={eventDetails.links} onChange={(selected_item) => {
                            setEventDetails({
                                links: selected_item
                            });
                        }} />
                    </InputSection>


                    <InputSection title="Service Flow" description="This date's order of service is edited on its Service Flow tab — not here.">
                        <Callout
                            type="info"
                            message={id
                                ? "Each date has its own editable flow. Finish or discard your changes here, then open the Service Flow tab to edit this date's flow."
                                : "Save this date to edit its Service Flow."}
                        />
                        {!!id && (
                            <SolidButton
                                size="sm"
                                type="events"
                                variant="outlined"
                                iconName="open-outline"
                                iconBefore
                                text="Open this date's Service Flow"
                                onPress={() => {
                                    if (hasUnsavedChanges) {
                                        Toast.show({ type: 'error', text2: 'Save or discard your changes first.' });
                                        return;
                                    }
                                    router.push({ pathname: `/events/instances/${id}`, params: { tab: 'serviceFlow' } });
                                }}
                                testID="service-flow-open-occurrence"
                            />
                        )}
                    </InputSection>

                    <InputSection title="Date & Time" description="Choose what time this event will start and end on each occurence">
                        <ToggleInput
                            selectedValues={[eventDetails.allDay ? "All Day" : "Time Slot"]}
                            items={["Time Slot", "All Day"]}
                            onItemChanged={(selected) => {
                                setEventDetails({
                                    allDay: selected == 'All Day',
                                })
                            }}
                        />

                        <DatePickerInput
                            title="Start Time"
                            mode={eventDetails.allDay ? "date" : "datetime"}
                            value={eventDetails.startTime || eventDetails.Event?.defaultStartTime}
                            onUpdate={(new_value) => {

                                if(new_value && typeof new_value == "object"){
                                    new_value.setSeconds(0);
                                }
                            
                                setEventDetails({
                                    startTime: new_value,
                                    endTime: eventDetails.endTime && eventDetails.endTime < new_value ?  calcEndDate(new_value, 0, 30, true) : eventDetails.endTime
                                })
                            }}
                        />

                        <DatePickerInput
                            title="End Time"
                            mode={eventDetails.allDay ? "date" : "datetime"}
                            minimumDate={eventDetails.endTime || eventDetails.Event?.defaultEndTime}
                            value={eventDetails.endTime}
                            onUpdate={(new_value) => {
                                if(new_value){
                                    new_value.setSeconds(0);
                                }

                                setEventDetails({
                                    endTime: new_value
                                })
                            }}
                        />

                        <View style={{flexDirection:"row", justifyContent:"space-between", flexWrap:"wrap"}}>
                            {/* <TouchableOpacity onPress={() => calcEndDate(eventDetails.startTime, 0, 15)} style={styles.addTimeButton}><Text style={{color:colors.secondary, fontSize:12}}>+15m</Text></TouchableOpacity> */}
                            <TouchableOpacity onPress={() => calcEndDate(eventDetails.startTime, 0, 30)} style={styles.addTimeButton}><Text style={{color:colors.text, fontSize:12, fontFamily:fonts.bold,}}>+30m</Text></TouchableOpacity>
                            <TouchableOpacity onPress={() => calcEndDate(eventDetails.startTime, 1, 0)} style={styles.addTimeButton}><Text style={{color:colors.text, fontSize:12, fontFamily:fonts.bold,}}>+1hr</Text></TouchableOpacity>
                            <TouchableOpacity onPress={() => calcEndDate(eventDetails.startTime, 2, 0)} style={styles.addTimeButton}><Text style={{color:colors.text, fontSize:12, fontFamily:fonts.bold,}}>+2hr</Text></TouchableOpacity>
                            <TouchableOpacity onPress={() => calcEndDate(eventDetails.startTime, 4, 0)} style={styles.addTimeButton}><Text style={{color:colors.text, fontSize:12, fontFamily:fonts.bold,}}>+4hr</Text></TouchableOpacity>
                            {/* <TouchableOpacity onPress={() => calcEndDate(eventDetails.startTime, 24, 0)} style={styles.addTimeButton}><Text style={{color:colors.text, fontSize:12, fontFamily:fonts.bold,}}>+24hr</Text></TouchableOpacity> */}
                        </View>
                    </InputSection>


                    <InputSection title="Music" description="Choose the songs you'd like in the worship setlist">
                        <SheetSelector type="songs" multiple={true} title="Songs" addScreenName="music/create" fromScreen={pathname} fromStoreKey={storeKey} value={eventDetails.songs || []} onChange={(selected_items) => {
                            setEventDetails({
                                songs: selected_items
                            });
                        }} />

                        {newKeyInputs}
                    </InputSection>

                    {
                        customFields?.length == 0 
                        ? <View></View> 
                        : <InputSection title="Custom Fields" description="Enter your custom field values for this occurence">
                            {customFields}
                        </InputSection>
                    }

                    <InputSection title="Registration" description="Register people for this event instance.">
                        <View style={{flexDirection:"row", alignItems:"center", justifyContent:"space-between", paddingVertical:8}}>
                            <Text style={{color:colors.text, fontFamily:fonts.bold, fontSize:14}}>Enable Registration</Text>
                            <Toggle
                                value={eventDetails.registrationEnabled ?? false}
                                onValueChange={(val) => setEventDetails({ registrationEnabled: val })}
                                disabled={!!extensionSource}
                                testID="registration-enabled-toggle"
                            />
                        </View>

                        {extensionSource && (
                            <Text style={{color:colors.textSecondary, fontFamily:fonts.body, fontSize:13, lineHeight:19, marginTop:4}}>
                                Registration is managed by {extensionSource.MainInstance?.nameOverride || extensionSource.MainInstance?.Event?.name}. This instance is an extension of another event&apos;s registration.
                            </Text>
                        )}

                        {eventDetails.registrationEnabled && (
                            <View style={{paddingVertical:8}}>
                                <Text style={{color:colors.textSecondary, fontFamily:fonts.body, fontSize:13, lineHeight:19}}>
                                    You can register people manually from the Event screen, or set up a form and automate registration through an Automation.
                                </Text>
                            </View>
                        )}
                    </InputSection>

                </View>

            </KeyboardAwareScrollView>

            <TooltipContainer modal={true}/>
        </ModalWrapper>
    );
}